/**
 * Service Tickets — Post-install service and issue management.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { PermissionGate } from "@/components/PermissionGate";
import { EmptyState } from "@/components/EmptyState";
import { useAppData } from "@/data/AppDataContext";
import { getServiceTicketBlockers } from "@/data/unifiedBlockers";
import { getServiceTicketNextSteps } from "@/data/nextStepEngine";
import { RecordHealthPanel } from "@/components/RecordHealthPanel";
import {
  SERVICE_TICKET_CATEGORIES, SERVICE_TICKET_PRIORITIES, SERVICE_TICKET_STATUSES,
  TICKET_STATUS_COLORS, TICKET_PRIORITY_COLORS,
  type ServiceTicketCategory, type ServiceTicketPriority, type ServiceTicketStatus, type ServiceTicket,
} from "@/data/serviceTicketTypes";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Plus, Search, Wrench, Clock, CheckCircle, ArrowRight, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { formatAddress } from "@/lib/addressUtils";

import { ServiceTicketService } from "@/services/service-ticket.service";
import { JobService } from "@/services/job.service";
import { getCurrentRepName } from "@/lib/repIdentity";
import { RESOLVE_SERVER_PAGING_ENABLED, RESOLVE_SERVICE_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import { isResolveServerPagedRoute, isResolveServiceServerPagedRoute, useBrowserLocationSnapshot } from "@/lib/routeScope";
import ResolveServiceTicketsServerPage from "./ResolveServiceTicketsServerPage";
import type { Job } from "@/data/types";

export default function ServiceTicketsPage() {
  const { pathname, search: routeSearch } = useBrowserLocationSnapshot();
  if (
    (RESOLVE_SERVER_PAGING_ENABLED && isResolveServerPagedRoute(pathname, routeSearch)) ||
    (RESOLVE_SERVICE_SERVER_PAGING_ENABLED && isResolveServiceServerPagedRoute(pathname, routeSearch))
  ) {
    return <ResolveServiceTicketsServerPage />;
  }
  return <ServiceTicketsLegacyPage />;
}

function ServiceTicketsLegacyPage() {
  const { setServiceTickets, setJobs } = useAppData();
  const [tickets, setTickets] = useState<ServiceTicket[]>([]);
  const [jobsList, setJobsList] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [tab, setTab] = useState("active");
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<ServiceTicket | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveStatus, setResolveStatus] = useState<"Resolved" | "Closed">("Resolved");
  const [resolutionNotes, setResolutionNotes] = useState("");

  // Create form state
  const [newJobId, setNewJobId] = useState("");
  const [newCategory, setNewCategory] = useState<ServiceTicketCategory>("Callback");
  const [newPriority, setNewPriority] = useState<ServiceTicketPriority>("Medium");
  const [newSubject, setNewSubject] = useState("");
  const [newDescription, setNewDescription] = useState("");

  const activeStatuses: ServiceTicketStatus[] = ["Open", "In Progress", "Awaiting Parts", "Scheduled"];
  const archivedStatuses: ServiceTicketStatus[] = ["Resolved", "Closed"];
  const isActive = tab === "active";
  const visibleStatuses = isActive ? activeStatuses : archivedStatuses;

  // Load initial data
  useEffect(() => {
    async function load() {
      setLoading(true);
      const [ticketsRes, jobsRes] = await Promise.all([
        ServiceTicketService.getAll(),
        JobService.getAll(),
      ]);

      if (ticketsRes.data) {
        setTickets(ticketsRes.data);
        setServiceTickets(ticketsRes.data);
      }
      if (jobsRes.data) {
        setJobsList(jobsRes.data);
        setJobs(jobsRes.data);
      }
      setLoading(false);
    }
    load();
  }, [setServiceTickets, setJobs]);

  const filtered = tickets.filter(t => {
    const inTab = visibleStatuses.includes(t.status);
    const matchSearch = !search ||
      t.customerName.toLowerCase().includes(search.toLowerCase()) ||
      t.id.toLowerCase().includes(search.toLowerCase()) ||
      t.subject.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || t.status === statusFilter;
    return inTab && matchSearch && matchStatus;
  });

  const activeCount = tickets.filter(t => activeStatuses.includes(t.status)).length;
  const archivedCount = tickets.filter(t => archivedStatuses.includes(t.status)).length;

  const currentUser = getCurrentRepName();

  const handleCreate = async () => {
    if (!newSubject.trim() || !newJobId) { toast.error("Subject and Job are required"); return; }

    // Find job by id or jobNumber
    const job = jobsList.find(j => j.id === newJobId || j.jobNumber === newJobId);
    if (!job) { toast.error("Selected job not found"); return; }

    try {
      const nextId = await ServiceTicketService.getNextTicketId();
      const currentUser = getCurrentRepName();
      const ticket: ServiceTicket = {
        id: nextId,
        jobId: job.jobNumber || job.id,
        customerId: job ? encodeURIComponent(job.customerName) : "",
        customerName: job?.customerName || "",
        customerPhone: job?.phone || "",
        customerEmail: job?.email || "",
        address: job ? formatAddress(job.address) : "",
        category: newCategory,
        priority: newPriority,
        status: "Open",
        subject: newSubject.trim(),
        description: newDescription.trim(),
        assignedTo: "",
        createdBy: currentUser,
        createdAt: new Date().toISOString().split("T")[0],
        updatedAt: new Date().toISOString().split("T")[0],
        activities: [{ id: `act-${Date.now()}`, action: "Ticket created", user: currentUser, createdAt: new Date().toISOString() }],
        notes: "",
      };

      const result = await ServiceTicketService.create(ticket);
      if (result.data) {
        setTickets(prev => [result.data!, ...prev]);
        setServiceTickets(prev => [result.data!, ...prev]);
        setCreateOpen(false);
        setNewSubject("");
        setNewDescription("");
        setNewJobId("");
        toast.success(`Service ticket ${result.data.id} created`);
      } else {
        toast.error("Failed to create ticket");
      }
    } catch (err) {
      toast.error("Error creating ticket");
      console.error(err);
    }
  };

  const handleStatusChange = async (newStatus: ServiceTicketStatus) => {
    if (!selectedTicket) return;
    try {
      const result = await ServiceTicketService.update(selectedTicket.id, { status: newStatus });
      if (result.data) {
        setTickets(prev => prev.map(t => t.id === selectedTicket.id ? result.data! : t));
        setServiceTickets(prev => prev.map(t => t.id === selectedTicket.id ? result.data! : t));
        setSelectedTicket(result.data);

        // Add activity
        await ServiceTicketService.addActivity(selectedTicket.id, {
          id: `act-${Date.now()}`,
          action: `Status changed to ${newStatus}`,
          user: currentUser,
          createdAt: new Date().toISOString(),
        });
        toast.success(`Status updated to ${newStatus}`);
      }
    } catch (err) {
      toast.error("Failed to update status");
      console.error(err);
    }
  };

  const handleResolveClose = async () => {
    if (!selectedTicket) return;
    try {
      const payload: Partial<ServiceTicket> = {
        status: resolveStatus,
        resolution: resolutionNotes,
        resolvedAt: new Date().toISOString().split("T")[0],
        resolvedBy: currentUser,
      };
      const result = await ServiceTicketService.update(selectedTicket.id, payload);
      if (result.data) {
        setTickets(prev => prev.map(t => t.id === selectedTicket.id ? result.data! : t));
        setServiceTickets(prev => prev.map(t => t.id === selectedTicket.id ? result.data! : t));
        setSelectedTicket(result.data);

        // Add activity
        await ServiceTicketService.addActivity(selectedTicket.id, {
          id: `act-${Date.now()}`,
          action: `Ticket ${resolveStatus.toLowerCase()}`,
          user: currentUser,
          createdAt: new Date().toISOString(),
        });

        setResolveOpen(false);
        setResolutionNotes("");
        toast.success(`Ticket ${resolveStatus.toLowerCase()}`);
      }
    } catch (err) {
      toast.error("Failed to resolve/close ticket");
      console.error(err);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading service tickets…</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Service Tickets</h1>
          <p className="text-xs text-muted-foreground mt-0.5">{activeCount} active · {archivedCount} resolved</p>
        </div>
        <PermissionGate permission="issues_create">
          <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
            <Plus className="h-3.5 w-3.5" /> New Ticket
          </Button>
        </PermissionGate>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Open", value: tickets.filter(t => t.status === "Open").length, icon: Clock, color: "text-info" },
          { label: "In Progress", value: tickets.filter(t => t.status === "In Progress").length, icon: Wrench, color: "text-warning" },
          { label: "Urgent", value: tickets.filter(t => t.priority === "Urgent" && activeStatuses.includes(t.status)).length, icon: AlertTriangle, color: "text-destructive" },
          { label: "Resolved", value: archivedCount, icon: CheckCircle, color: "text-success" },
        ].map(kpi => (
          <Card key={kpi.label} className="border shadow-none">
            <CardContent className="p-3 text-center">
              <kpi.icon className={`h-4 w-4 mx-auto mb-1 ${kpi.color}`} />
              <p className="text-xl font-bold">{kpi.value}</p>
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-medium">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={v => { setTab(v); setStatusFilter("all"); }}>
        <TabsList>
          <TabsTrigger value="active" className="text-xs gap-1.5"><Wrench className="h-3.5 w-3.5" /> Active ({activeCount})</TabsTrigger>
          <TabsTrigger value="archived" className="text-xs gap-1.5"><CheckCircle className="h-3.5 w-3.5" /> Resolved ({archivedCount})</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input placeholder="Search tickets…" value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All {isActive ? "Active" : "Resolved"}</SelectItem>
            {visibleStatuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="shadow-none">
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">ID</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Subject</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Customer</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Category</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Priority</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Status</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Job</th>
                <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground">Created</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id} className="border-b last:border-0 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => setSelectedTicket(t)}>
                  <td className="px-4 py-3 font-medium text-primary">{t.id}</td>
                  <td className="px-4 py-3 font-medium max-w-[200px] truncate">{t.subject}</td>
                  <td className="px-4 py-3">
                    <Link to={`/customers/${encodeURIComponent(t.customerName)}`} className="hover:underline text-primary" onClick={e => e.stopPropagation()}>{t.customerName}</Link>
                  </td>
                  <td className="px-4 py-3">{t.category}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${TICKET_PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${TICKET_STATUS_COLORS[t.status]}`}>{t.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Link to={`/jobs/${t.jobId}`} className="text-primary hover:underline inline-flex items-center gap-1" onClick={e => e.stopPropagation()}>{t.jobId} <ArrowRight className="h-3 w-3" /></Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{t.createdAt}</td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-0">
                  <EmptyState
                    icon={Wrench}
                    title="No service tickets"
                    description="Tickets appear here when post-install issues are reported. You can also create one manually."
                    actionLabel="Create Ticket"
                    onAction={() => setCreateOpen(true)}
                  />
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Ticket Detail Dialog */}
      {selectedTicket && (
        <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="h-4 w-4" /> {selectedTicket.id} — {selectedTicket.subject}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-muted-foreground">Customer:</span> <span className="font-medium">{selectedTicket.customerName}</span></div>
                <div><span className="text-muted-foreground">Job:</span> <Link to={`/jobs/${selectedTicket.jobId}`} className="text-primary hover:underline">{selectedTicket.jobId}</Link></div>
                <div><span className="text-muted-foreground">Category:</span> {selectedTicket.category}</div>
                <div><span className="text-muted-foreground">Assigned:</span> {selectedTicket.assignedTo || "Unassigned"}</div>
                <div><span className="text-muted-foreground">Priority:</span> <span className={`inline-flex px-2 py-0.5 rounded-md border text-[10px] font-semibold ${TICKET_PRIORITY_COLORS[selectedTicket.priority]}`}>{selectedTicket.priority}</span></div>
                <div><span className="text-muted-foreground">Status:</span>
                  <Select value={selectedTicket.status} onValueChange={v => handleStatusChange(v as ServiceTicketStatus)}>
                    <SelectTrigger className="inline-flex h-7 text-xs w-32 ml-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SERVICE_TICKET_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {selectedTicket.description && <p className="text-xs text-muted-foreground">{selectedTicket.description}</p>}
              <RecordHealthPanel
                title="Ticket Health"
                blockers={getServiceTicketBlockers(selectedTicket)}
                nextSteps={getServiceTicketNextSteps(selectedTicket)}
                completeness={[
                  { label: "Assigned", done: !!selectedTicket.assignedTo },
                  { label: "Description", done: !!selectedTicket.description },
                  { label: "Scheduled", done: !!selectedTicket.scheduledDate || !["Scheduled", "In Progress"].includes(selectedTicket.status) },
                  { label: "Resolution", done: !!selectedTicket.resolution },
                ]}
                compact
              />
              <div className="flex gap-2 pt-2">
                {!["Resolved", "Closed"].includes(selectedTicket.status) && (
                  <PermissionGate permission="issues_edit">
                    <Button variant="outline" size="sm" onClick={() => setResolveOpen(true)}>
                      Resolve / Close
                    </Button>
                  </PermissionGate>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Resolve/Close Dialog */}
      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle className="h-4 w-4" /> Resolve or Close Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Final Status</Label>
              <Select value={resolveStatus} onValueChange={v => setResolveStatus(v as "Resolved" | "Closed")}>
                <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Resolved">Resolved</SelectItem>
                  <SelectItem value="Closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Resolution Notes *</Label>
              <Textarea value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)} placeholder="Describe how the issue was resolved…" className="mt-1" rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResolveOpen(false)}>Cancel</Button>
            <PermissionGate permission="issues_edit">
              <Button onClick={handleResolveClose} disabled={!resolutionNotes.trim()}>Confirm</Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4" /> New Service Ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Linked Job *</Label>
              <Select value={newJobId} onValueChange={setNewJobId}>
                <SelectTrigger className="mt-1 text-xs"><SelectValue placeholder="Select job…" /></SelectTrigger>
                <SelectContent>
                  {jobsList.filter(j => j.currentStage === "Completed" || j.currentStage === "QA Review").map(j => (
                    <SelectItem key={j.id} value={j.jobNumber || j.id}>{(j.jobNumber || j.id)} — {j.customerName}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={newCategory} onValueChange={v => setNewCategory(v as ServiceTicketCategory)}>
                  <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TICKET_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={newPriority} onValueChange={v => setNewPriority(v as ServiceTicketPriority)}>
                  <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICE_TICKET_PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Subject *</Label>
              <Input value={newSubject} onChange={e => setNewSubject(e.target.value)} placeholder="Brief description" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={newDescription} onChange={e => setNewDescription(e.target.value)} placeholder="Detailed description…" className="mt-1" rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <PermissionGate permission="issues_create">
              <Button onClick={handleCreate} disabled={!newSubject.trim() || !newJobId}>Create Ticket</Button>
            </PermissionGate>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
